import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSText
} from "@wingspanhq/fe-component-library";
import { IRequirementDefinitionResponse } from "@wingspanhq/payments/dist/interfaces";
import { RequirementDefinitionStatus } from "@wingspanhq/payments/dist/interfaces/requirement";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import React from "react";
import { ErrorContextKey } from "../../../services/platform";
import { DefaultActionDetails } from "../components/DefaultActionDetails";
import { useMutationUpdateRequirementDefinition } from "../../../query/requirementDefinitions/mutation/useMutationUpdateRequirementDefinition";

export interface RestoreRequirementModalProps {
  requirementDefinition: IRequirementDefinitionResponse;
  engagementLength: number;
  onClose: () => void;
}

export const RestoreRequirementModal: React.FC<
  RestoreRequirementModalProps
> = ({ requirementDefinition, onClose, engagementLength }) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateRequirementDefinition, updateRequirementDefinitionMeta] =
    useMutationUpdateRequirementDefinition(
      requirementDefinition.requirementDefinitionId
    );

  const handleRestore = async () => {
    await updateRequirementDefinition(
      {
        status: RequirementDefinitionStatus.Active
      },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Requirement restored"
          });
          onClose();
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to restore this requirement?
      </WSText.ParagraphSm>
      <DefaultActionDetails
        requirementDefinition={requirementDefinition}
        engagementLength={engagementLength}
      />

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Restoring requirement - what to expect:"
      >
        Requirement can once again be added to engagements and assigned to
        contractors to complete.
      </WSAlert>

      <WSErrorMessage
        error={updateRequirementDefinitionMeta.error}
        mb="XL"
        contextKey={ErrorContextKey.Other}
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Restore requirement",
            loading: updateRequirementDefinitionMeta.isLoading,
            onAsyncClick: handleRestore,
            kind: "Primary"
          }
        ]}
      />
    </>
  );
};

export const useRestoreRequirementModal = () => {
  return useWSModal(RestoreRequirementModal, {
    title: "Restore requirement",
    size: "M"
  });
};
